"use client";

import * as React from "react";
import { cn } from "@/lib/utils";

import "./loader.css";

export interface LoaderProps extends React.HTMLAttributes<HTMLDivElement> {
  size?: "sm" | "DEFAULT" | "lg" | "xl";
  variant?: "spinner" | "dots" | "pulse" | "bars";
  className?: string;
  text?: string;
}

const Loader = React.forwardRef<HTMLDivElement, LoaderProps>(
  (
    {
      size = "DEFAULT",
      variant = "bars",
      className,
      text,
      ...props
    },
    ref
  ) => {
    const sizeClasses = {
      sm: "h-4 w-4",
      DEFAULT: "h-8 w-8",
      lg: "h-12 w-12",
      xl: "h-16 w-16",
    };

    const textSizeClasses = {
      sm: "text-xs",
      DEFAULT: "text-sm",
      lg: "text-base",
      xl: "text-lg",
    };

    if (variant === "bars") {
      const barsScale = {
        sm: "scale-[0.2]",
        DEFAULT: "scale-[0.4]",
        lg: "scale-[0.6]",
        xl: "scale-[0.8]",
      };

      return (
        <div
          ref={ref}
          className={cn("bars-loader-wrapper gap-3", className)}
          {...props}
        >
          {/* A container to scale the fixed 200x100 3D loader */}
          <div className={cn("origin-center transition-transform transform", barsScale[size])}>
            <div className="bars-loader">
              <div className="bars-bar"></div>
              <div className="bars-bar"></div>
              <div className="bars-bar"></div>
              <div className="bars-bar"></div>
              <div className="bars-bar"></div>
              <div className="bars-bar"></div>
              <div className="bars-bar"></div>
              <div className="bars-bar"></div>
              <div className="bars-bar"></div>
              <div className="bars-bar"></div>
            </div>
          </div>
          <p className={cn("text-gray-600 dark:text-gray-400 mt-2 font-medium tracking-widest", textSizeClasses[size])}>
            {text || "Preparing your data… please wait"}
          </p>
        </div>
      );
    }

    if (variant === "spinner") {
      return (
        <div
          ref={ref}
          className={cn("flex flex-col items-center justify-center gap-3", className)}
          {...props}
        >
          <svg
            className={cn("animate-spin text-primary", sizeClasses[size])}
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          {text && (
            <p className={cn("text-gray-600 dark:text-gray-400", textSizeClasses[size])}>
              {text}
            </p>
          )}
        </div>
      );
    }

    if (variant === "dots") {
      const dotSize = {
        sm: "h-1.5 w-1.5",
        DEFAULT: "h-2 w-2",
        lg: "h-3 w-3",
        xl: "h-4 w-4",
      };

      return (
        <div
          ref={ref}
          className={cn("flex flex-col items-center justify-center gap-3", className)}
          {...props}
        >
          <div className="flex items-center gap-1">
            <div
              className={cn(
                "rounded-full bg-primary animate-bounce",
                dotSize[size]
              )}
              style={{ animationDelay: "0ms" }}
            />
            <div
              className={cn(
                "rounded-full bg-primary animate-bounce",
                dotSize[size]
              )}
              style={{ animationDelay: "150ms" }}
            />
            <div
              className={cn(
                "rounded-full bg-primary animate-bounce",
                dotSize[size]
              )}
              style={{ animationDelay: "300ms" }}
            />
          </div>
          {text && (
            <p className={cn("text-gray-600 dark:text-gray-400", textSizeClasses[size])}>
              {text}
            </p>
          )}
        </div>
      );
    }

    // pulse variant
    return (
      <div
        ref={ref}
        className={cn("flex flex-col items-center justify-center gap-3", className)}
        {...props}
      >
        <div
          className={cn(
            "rounded-full bg-primary animate-pulse",
            sizeClasses[size]
          )}
        />
        {text && (
          <p className={cn("text-gray-600 dark:text-gray-400", textSizeClasses[size])}>
            {text}
          </p>
        )}
      </div>
    );
  }
);

Loader.displayName = "Loader";

export { Loader };
